import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useUnifiedChat } from '@/hooks/useUnifiedChat';
import { chatService } from '@/services/chatService';
import { chatCategories, ChatCategory } from '@/types/chatCategories';
import UserSearchInput from './UserSearchInput';
import SelectedUsersSummary from './SelectedUsersSummary';
import TeamSelector from './TeamSelector';
import { TeamConnection } from '@/types/chat';
import { toast } from 'sonner';

interface CreateChatModalProps {
  isOpen: boolean;
  onClose: () => void;
  onGroupCreated?: () => void;
}

const CreateChatModal = ({ isOpen, onClose, onGroupCreated }: CreateChatModalProps) => {
  const [groupName, setGroupName] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<ChatCategory>('general');
  const [chatType, setChatType] = useState<'open' | 'read_only'>('open');
  const [selectedUsers, setSelectedUsers] = useState<any[]>([]);
  const [availableUsers, setAvailableUsers] = useState<any[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<TeamConnection | null>(null);
  const [useTeamMembers, setUseTeamMembers] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const { createChatGroup, user, loadChatGroups } = useUnifiedChat();

  // Determine if user is admin or management role
  const isAdmin = user && ((user as any).role === 'admin' || (user as any).role === 'support');
  const isManagement = user && ['coach', 'team-owner', 'venue'].includes((user as any).role);

  // Filter categories based on admin status
  const visibleCategories = chatCategories.filter(category => 
    !category.adminOnly || isAdmin
  );

  // Load available users when modal opens
  useEffect(() => {
    const loadUsers = async () => {
      if (!isOpen) return;
      
      try {
        setLoadingUsers(true);
        console.log('Loading available users for group creation');
        
        const allUsers = await chatService.getAllUsers();
        console.log('All users loaded from chatService:', allUsers);
        
        const currentUserId = user?.id;
        const otherUsers = allUsers.filter(u => u.id !== currentUserId);
        console.log('Available users (excluding current user):', otherUsers);
        
        setAvailableUsers(otherUsers);
      } catch (error) {
        console.error('Error loading users:', error);
        toast.error('Failed to load users');
        setAvailableUsers([]);
      } finally {
        setLoadingUsers(false);
      }
    };

    loadUsers();
  }, [isOpen, user?.id]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setGroupName('');
      setSelectedCategory('general');
      setChatType('open');
      setSelectedUsers([]);
      setSelectedTeam(null);
      setUseTeamMembers(false);
    }
  }, [isOpen]);

  const handleCreateGroup = async () => {
    if (!groupName.trim()) {
      toast.error('Please enter a group name');
      return;
    }

    if (!selectedCategory) {
      toast.error('Please select a category');
      return;
    }

    if (!user) {
      toast.error('You must be logged in to create a group');
      return;
    }

    // Determine users to add
    let usersToAdd = selectedUsers;
    if (useTeamMembers && selectedTeam) {
      // Filter out the current user from team members
      usersToAdd = selectedTeam.users.filter(u => u.id !== user.id);
    }

    if (usersToAdd.length === 0 && !useTeamMembers) {
      toast.error('Please add at least one member to the group');
      return;
    }

    try {
      setLoading(true);
      console.log('Creating group with name:', groupName, 'category:', selectedCategory, 'type:', chatType);
      
      const userIds = usersToAdd.map(u => u.id);
      console.log('User IDs to add:', userIds);
      
      // Create group with category prefix and chat type
      const categoryInfo = chatCategories.find(cat => cat.id === selectedCategory);
      const typePrefix = chatType === 'read_only' ? '[READ ONLY] ' : '';
      const teamPrefix = selectedTeam ? `[${selectedTeam.team_name}] ` : '';
      const fullGroupName = `${typePrefix}${teamPrefix}[${categoryInfo?.name}] ${groupName}`;
      
      console.log('Full group name:', fullGroupName);
      
      // Ensure current user is set in the service before creating group
      if (typeof (window as any).setCurrentUserInChatService === 'function') {
        (window as any).setCurrentUserInChatService(user);
      }
      
      // Create the group with users
      console.log('📤 Attempting to create group with users:', userIds);
      const newGroup = await createChatGroup(fullGroupName, userIds);
      
      if (newGroup) {
        console.log('✅ Group created successfully:', newGroup.chat_id, 'reloading chat groups');
        
        // Wait a bit to ensure the group is fully created
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Reload chat groups to show the new group
        await loadChatGroups();
        
        // Call the callback to trigger parent component refresh
        if (onGroupCreated) {
          onGroupCreated();
        }
        
        // Reset form and close modal
        handleClose();
        
        toast.success(`Chat group "${fullGroupName}" created successfully!`);
      } else {
        console.error('❌ createChatGroup returned null');
        throw new Error('Failed to create group - group creation returned null. Check console for details.');
      }
      
    } catch (error) {
      console.error('Error creating chat group:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      toast.error(`Failed to create chat group: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setGroupName('');
    setSelectedCategory('general');
    setChatType('open');
    setSelectedUsers([]);
    setSelectedTeam(null);
    setUseTeamMembers(false);
    onClose();
  };

  const handleTeamSelect = (team: TeamConnection) => {
    setSelectedTeam(team);
    setUseTeamMembers(true);
    // Clear manually selected users when using team
    setSelectedUsers([]);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-gray-800 border-gray-700 text-white max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create New Chat Group</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div>
            <Label htmlFor="category">Category</Label>
            <Select value={selectedCategory} onValueChange={(value: ChatCategory) => setSelectedCategory(value)}>
              <SelectTrigger className="bg-gray-700 border-gray-600 text-white mt-1">
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent className="bg-gray-700 border-gray-600">
                {visibleCategories.map((category) => (
                  <SelectItem key={category.id} value={category.id} className="text-white hover:bg-gray-600">
                    {category.name} - {category.description}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="groupName">Group Name</Label>
            <Input
              id="groupName"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              placeholder="Enter group name"
              className="bg-gray-700 border-gray-600 text-white mt-1"
              disabled={loading}
            />
          </div>

          {/* Chat Type Selection */}
          <div>
            <Label>Chat Type</Label>
            <RadioGroup value={chatType} onValueChange={(value: 'open' | 'read_only') => setChatType(value)} className="mt-2">
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="open" id="open" className="border-gray-500" />
                <Label htmlFor="open" className="text-sm">Open Chat - Everyone can message</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="read_only" id="read_only" className="border-gray-500" />
                <Label htmlFor="read_only" className="text-sm">Read Only - Only creator can message</Label>
              </div>
            </RadioGroup>
          </div>

          {/* Team Selection for Management Roles */}
          {isManagement && selectedCategory === 'team' && (
            <div>
              <div className="flex items-center space-x-2 mb-2">
                <input
                  type="checkbox"
                  id="useTeam"
                  checked={useTeamMembers}
                  onChange={(e) => {
                    setUseTeamMembers(e.target.checked);
                    if (!e.target.checked) {
                      setSelectedTeam(null);
                    }
                  }}
                  className="rounded"
                />
                <Label htmlFor="useTeam" className="text-sm">Add entire team</Label>
              </div>
              
              {useTeamMembers && (
                <TeamSelector
                  userId={user?.id || ''}
                  onTeamSelect={handleTeamSelect}
                  selectedTeam={selectedTeam}
                />
              )}
            </div>
          )}

          {/* Manual User Selection (only if not using team members) */}
          {!useTeamMembers && (
            <div>
              {loadingUsers ? (
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-sports-green"></div>
                  <span className="ml-2 text-gray-400">Loading users...</span>
                </div>
              ) : (
                <UserSearchInput
                  availableUsers={availableUsers}
                  selectedUsers={selectedUsers}
                  onUserSelect={(user) => {
                    if (!selectedUsers.find(u => u.id === user.id)) {
                      console.log('Adding user to selection:', user);
                      setSelectedUsers(prev => [...prev, user]);
                    }
                  }}
                  onUserRemove={(userId) => {
                    console.log('Removing user from selection:', userId);
                    setSelectedUsers(prev => prev.filter(u => u.id !== userId));
                  }}
                />
              )}
              
              {selectedUsers.length > 0 && (
                <SelectedUsersSummary
                  selectedUsers={selectedUsers}
                  onRemoveUser={(userId) => {
                    setSelectedUsers(prev => prev.filter(u => u.id !== userId));
                  }}
                />
              )}
            </div>
          )}

          <div className="flex gap-2 pt-4">
            <Button
              onClick={handleCreateGroup}
              disabled={loading || !groupName.trim() || !selectedCategory || !user}
              className="flex-1 bg-sports-green hover:bg-sports-green/90 text-black"
              type="button"
            >
              {loading ? 'Creating...' : 'Create Group'}
            </Button>
            <Button
              onClick={handleClose}
              variant="outline"
              className="flex-1 border-gray-600 text-white hover:bg-gray-700"
              disabled={loading}
              type="button"
            >
              Cancel
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CreateChatModal;
